import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SecureImage from '../Media/SecureImage';
import { toTagList } from '../../utils/tags';

interface OriginalsContentCardProps {
  id: string;
  title: string;
  thumbnail?: string;
  type: 'cinema' | 'content' | 'podcast' | 'animation' | 'sneak-peek' | 'live-video' | 'series';
  /**
   * Accepted in whatever shape it arrives. The backend returns a comma-separated
   * string; callers that build their own list pass an array. Typing this as string[]
   * alone is what let a string through to a .map() call unnoticed.
   */
  tags?: string[] | string | null;
  category?: string;
  description?: string;
  duration?: number;
  views?: number;
  likes?: number;
  size?: 'small' | 'medium' | 'large';
  featured?: boolean;
  season?: number;
  episode?: number;
  style?: string;
  complexity?: string;
  // Series-related props
  isPartOfSeries?: boolean;
  seriesId?: string;
  seriesTitle?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  totalSeasons?: number;
  totalEpisodes?: number;
  seriesType?: 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES';
}

const ContentCard: React.FC<OriginalsContentCardProps> = ({
  id,
  title,
  thumbnail,
  type,
  tags: rawTags,
  category: _category,
  isPartOfSeries = false,
  seriesId: _seriesId,
  seriesTitle,
  seasonNumber: _seasonNumber,
  episodeNumber: _episodeNumber,
  totalSeasons,
  totalEpisodes,
  seriesType,
}) => {
  const tags = toTagList(rawTags);
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate route based on content type
  const getContentRoute = () => {
    // If this is series content, route to the series ID 
    // The UnifiedPlayer will handle series navigation and episode display
    switch (type) {
      case 'cinema':
        return `/media/cinema/${id}`;
      case 'content':
        return `/media/contents/${id}`;
      case 'podcast':
        return `/podcasts/${id}`;
      case 'animation':
        return `/animations/${id}`;
      case 'sneak-peek':
        return `/sneak-peeks/${id}`;
      case 'live-video':
        return `/live/${id}`;
      case 'series':
        // For series, route to /series/{seriesId} - UnifiedPlayer will handle it
        return `/series/${id}`;
      default: {
        // Fallback - try to determine from the current path or default to content
        const currentPath = window.location.pathname;
        if (currentPath.includes('/media/cinema')) return `/media/cinema/${id}`;
        if (currentPath.includes('/podcasts')) return `/podcasts/${id}`;
        if (currentPath.includes('/animations')) return `/animations/${id}`;
        if (currentPath.includes('/sneak-peeks')) return `/sneak-peeks/${id}`;
        return `/media/contents/${id}`;
      }
    }
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(getContentRoute());
  };

  const handleCardClick = () => {
    navigate(getContentRoute());
  };

  const getTypeColor = () => {
    switch (type) {
      case 'cinema':
        return 'bg-red-500';
      case 'content':
        return 'bg-blue-500';
      case 'podcast':
        return 'bg-green-500';
      case 'animation':
        return 'bg-orange-500';
      case 'sneak-peek':
        return 'bg-orange-500';
      case 'live-video':
        return 'bg-red-500';
      case 'series':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTypeLabel = () => {
    // If it's part of a series, show series type
    if (isPartOfSeries && seriesType) {
      switch (seriesType) {
        case 'CINEMA_SERIES':
          return 'Cinema Series';
        case 'CONTENT_SERIES':
          return 'Content Series';
        case 'PODCAST_SERIES':
          return 'Podcast Series';
        case 'ANIMATION_SERIES':
          return 'Animation Series';
        default:
          return 'Series';
      }
    }
    
    // Otherwise show regular content type
    switch (type) {
      case 'cinema':
        return 'Cinema';
      case 'content':
        return 'Content';
      case 'podcast':
        return 'Podcast';
      case 'animation':
        return 'Animation';
      case 'sneak-peek':
        return 'Sneak Peek';
      case 'live-video':
        return 'Live';
      case 'series':
        return 'Series';
      default:
        return 'Content';
    }
  };

  // Generate fallback thumbnail
  const getFallbackThumbnail = () => {
    return `https://images.unsplash.com/photo-1616530940355-351fabd9524b?w=400&h=225&fit=crop&auto=format&q=80`;
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 overflow-hidden cursor-pointer group flex flex-col h-80 hover-float"
      onClick={handleCardClick}
    >
      {/* Thumbnail - Landscape aspect ratio */}
      <div className="relative aspect-video overflow-hidden bg-gray-100 flex-shrink-0">
        {!imageError ? (
          <SecureImage
            src={thumbnail || getFallbackThumbnail()}
            alt={title}
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <div className="text-2xl mb-2">🎬</div>
              <div className="text-sm">No Image</div>
            </div>
          </div>
        )}

        {/* Play Button Overlay */}
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button
            onClick={handlePlayClick}
            className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-3 transform scale-100 hover:scale-110 transition-all duration-200 shadow-lg"
            aria-label={`Play ${title}`}
          >
            <Play className="w-6 h-6 ml-1" fill="currentColor" />
          </button>
        </div>

        {/* Content Type Badge */}
        <div className={`absolute top-3 left-3 ${getTypeColor()} text-white px-2 py-1 rounded text-xs font-medium`}>
          {getTypeLabel()}
        </div>

        {/* Category Badge */}
        {/* {category && (
          <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
            {category}
          </div>
        )} */}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1 min-h-0">
        {/* Title */}
        <h3 
          className="font-semibold text-gray-900 text-lg mb-2 group-hover:text-orange-600 transition-colors duration-200 overflow-hidden"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.4em',
            maxHeight: '2.8em'
          }}
          title={isPartOfSeries && seriesTitle ? seriesTitle : title}
        >
          {isPartOfSeries && seriesTitle ? seriesTitle : title}
        </h3>

        {/* Series Info or Tags */}
        <div className="flex flex-wrap gap-1 overflow-hidden">
          {/* Series Stats */}
          {isPartOfSeries && totalSeasons && totalEpisodes && (
            <>
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0">
                {totalSeasons} Season{totalSeasons > 1 ? 's' : ''}
              </span>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0">
                {totalEpisodes} Episode{totalEpisodes > 1 ? 's' : ''}
              </span>
            </>
          )}
          
          {/* Regular Tags */}
          {tags && tags.length > 0 && (
            <>
              {tags.slice(0, isPartOfSeries ? 1 : 3).map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium hover:bg-orange-100 hover:text-orange-700 transition-colors duration-200 truncate max-w-20"
                  title={tag}
                >
                  {tag}
                </span>
              ))}
              {tags.length > (isPartOfSeries ? 1 : 3) && (
                <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs flex-shrink-0">
                  +{tags.length - (isPartOfSeries ? 1 : 3)}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentCard;